/**
 * How much loan the business's own cash flow supports, the way a lender sizes it.
 *
 * Tax returns are the source of truth. For each year we start from net income
 * and add back what a lender typically allows — depreciation (not cash),
 * interest (the new loan's payment replaces it), the owner's salary when the
 * owner is selling or will not draw it, and one-time items — then subtract
 * payments on debt that stays. That is the cash available for the new loan.
 *
 *   max monthly payment = cash flow / DSCR target / 12
 *   max loan            = PV(rate / 12, term, max monthly payment)
 *
 * The LOWEST year sets the limit, because that is how most lenders underwrite.
 * When every year grew, some lenders will size on the latest year instead, so
 * that figure is reported too — never silently used in place of the lowest.
 *
 * Pure. No rates are invented here: rate and term come from the caller, which
 * gets them from `estimate7a`.
 */

import { monthlyPayment, principalForPayment, round2 } from './annuity.js';

/** Ratios of revenue (%), from src/data/industries.json. */
export interface Benchmarks {
  cogsPct: number;
  opexPct: number;
  ebitdaPct: number;
  netIncomePct?: number;
  depreciationPct?: number;
  interestPct?: number;
  growthPct?: number;
}

export type DealType = 'startup' | 'existing' | 'acquisition';

/** One tax year (or year to date), as filed. */
export interface YearFigures {
  label: string;
  /** Months covered. 12 for a tax year; fewer for year to date, which is annualised. */
  months?: number;
  revenue?: number;
  cogs?: number;
  operatingExpenses?: number;
  netIncome: number;
  /** Owner's or officers' pay, as deducted on the return. */
  ownerSalary?: number;
  depreciation?: number;
  /** Interest expense deducted on the return. */
  interest?: number;
  /** Non-recurring items: a one-time expense is positive, one-time income negative. */
  oneTimeItems?: number;
  otherAddBacks?: number;
}

export interface CashFlowInput {
  dealType: DealType;
  /** Oldest first. Not needed for a startup. */
  years: YearFigures[];
  /** Startup only: first stabilised year's revenue. */
  projectedRevenue?: number;
  benchmark?: Benchmarks;
  /** Add the owner's salary back. Defaults to true when buying a business. */
  addBackOwnerSalary?: boolean;
  /** What the owner (or a manager) still needs to be paid; deducted when salary is added back. */
  replacementSalary?: number;
  /** Household income from outside the business (a spouse's salary, say). */
  outsideIncome?: number;
  /** Annual payments on debt that stays after the new loan. */
  existingDebtService?: number;
  dscrTarget: number;
  ratePct: number;
  termMonths: number;
  /** When given, coverage is checked at this amount. */
  requestedLoan?: number;
}

export interface YearResult {
  label: string;
  annualised: boolean;
  /** Net income plus allowed add-backs, annualised. */
  adjustedCashFlow: number;
  /** After payments on debt that stays, plus outside income. What the new loan can use. */
  availableForNewDebt: number;
  sde: number;
  maxMonthlyPayment: number;
  maxLoan: number;
  /** Coverage at the requested loan, when one was given. */
  dscr: number | null;
  meetsTarget: boolean | null;
  addBacks: { label: string; amount: number }[];
}

export interface BenchmarkFlag {
  metric: 'cogs' | 'opex' | 'margin';
  label: string;
  yoursPct: number;
  typicalPct: number;
  note: string;
}

/**
 * SBA's own minimum coverage test under SOP 50 10 8.1 (effective 1 Oct 2026),
 * which is looser than the lowest-year sizing many lenders apply on top.
 */
export interface SbaRule {
  label: string;
  minDscr: number;
  /** Which cash flow SBA measures: "latest full year (2025)", "2-year average"... */
  basis: string;
  cashFlow: number;
  maxLoan: number;
  requestedDscr: number | null;
  meets: boolean | null;
  citation: string;
  /** False when read in lender summaries rather than the SOP text itself. */
  verified: boolean;
}

export interface CashFlowResult {
  dealType: DealType;
  years: YearResult[];
  /** The year that sets the limit. */
  limitingYear: string;
  /** Sized on the lowest year — what most lenders do. */
  maxLoan: number;
  maxMonthlyPayment: number;
  /** Sized on the latest full year, only when every year grew. */
  growthMaxLoan: number | null;
  grewEveryYear: boolean;
  requestedLoan: number | null;
  requestedMonthlyPayment: number | null;
  /** Lowest coverage across the years at the requested loan. */
  requestedMinDscr: number | null;
  verdict: 'qualifies' | 'borderline' | 'short' | null;
  isProjection: boolean;
  benchmarkFlags: BenchmarkFlag[];
  sbaRule: SbaRule | null;
  notes: string[];
}

/** 7(a) Small loans are $350,000 or less. */
const SMALL_LOAN_MAX = 350_000;

function sbaRuleFor(input: CashFlowResult['dealType'], years: YearResult[], requested: number | null, sized: number, newDebtService: number | null, calc: CashFlowInput): SbaRule | null {
  const full = years.filter((y) => !y.annualised);
  let rule: Omit<SbaRule, 'maxLoan' | 'requestedDscr' | 'meets'>;
  if (input === 'acquisition') {
    const latest = full.at(-1);
    if (!latest) return null;
    const prior = full.at(-2);
    const avg = prior ? (latest.availableForNewDebt + prior.availableForNewDebt) / 2 : -Infinity;
    const useAvg = avg > latest.availableForNewDebt;
    rule = {
      label: 'SBA change-of-ownership minimum', minDscr: 1.25,
      basis: useAvg ? `average of ${prior!.label} and ${latest.label}` : `latest full year (${latest.label})`,
      cashFlow: Math.round(useAvg ? avg : latest.availableForNewDebt),
      citation: 'SOP 50 10 8.1, Appendix 15 (effective 1 Oct 2026): 1.25x on the last fiscal year or a two-year average; projections cannot be used',
      verified: false,
    };
  } else if (input === 'startup' || full.length === 0) {
    const y = years[0];
    if (!y) return null;
    rule = {
      label: 'SBA minimum for projections', minDscr: 1.15, basis: 'projected cash flow within 2 years of funding', cashFlow: y.availableForNewDebt,
      citation: 'SOP 50 10 8.1, Ch. 2 Underwriting: projections must show 1.15x within 2 years of loan funding', verified: true,
    };
  } else {
    const latest = full.at(-1)!;
    const small = (requested ?? sized) <= SMALL_LOAN_MAX;
    rule = {
      label: small ? 'SBA minimum for 7(a) Small loans' : 'SBA minimum for existing businesses',
      minDscr: small ? 1.1 : 1.15, basis: `latest full year (${latest.label})`, cashFlow: latest.availableForNewDebt,
      citation: small
        ? 'SOP 50 10 8.1, 7(a) Small loans: 1.10x on the last year-end statement, with interim statements showing no decline'
        : 'SOP 50 10 8.1, Ch. 2 Underwriting: 1.15x on historical cash flow after the SBA loan; global coverage at least 1.0x',
      verified: true,
    };
  }
  const maxMonthly = Math.max(0, rule.cashFlow / rule.minDscr / 12);
  const maxLoan = Math.round(Math.min(MAX_7A, principalForPayment(maxMonthly, calc.ratePct, calc.termMonths)));
  const requestedDscr = newDebtService ? round2(coverageRatio(rule.cashFlow, newDebtService)) : null;
  return { ...rule, maxLoan, requestedDscr, meets: requestedDscr === null ? null : requestedDscr >= rule.minDscr };
}

/** SBA 7(a) program maximum. */
export const MAX_7A = 5_000_000;

export function coverageRatio(cashFlow: number, annualDebtService: number): number {
  return annualDebtService > 0 ? cashFlow / annualDebtService : Infinity;
}

function sizeFor(available: number, input: CashFlowInput) {
  const maxMonthlyPayment = Math.max(0, available / input.dscrTarget / 12);
  const maxLoan = Math.min(MAX_7A, principalForPayment(maxMonthlyPayment, input.ratePct, input.termMonths));
  return { maxMonthlyPayment: round2(maxMonthlyPayment), maxLoan: Math.round(maxLoan) };
}

function yearResult(y: YearFigures, input: CashFlowInput, addSalary: boolean, newDebtService: number | null): YearResult {
  const months = y.months ?? 12;
  if (months <= 0 || months > 12) throw new Error(`${y.label}: months must be 1 to 12`);
  const k = 12 / months;
  const addBacks: { label: string; amount: number }[] = [];
  const push = (label: string, v: number | undefined) => { if (v) addBacks.push({ label, amount: v }); };
  push('Depreciation and amortization', y.depreciation);
  push('Interest', y.interest);
  push('One-time items', y.oneTimeItems);
  push('Other add-backs', y.otherAddBacks);
  if (addSalary) {
    push("Owner's salary", y.ownerSalary);
    if (input.replacementSalary) push('Replacement salary', -input.replacementSalary * (months / 12));
  }
  const sde = (y.netIncome + (y.ownerSalary ?? 0) + (y.depreciation ?? 0) + (y.interest ?? 0)
    + (y.oneTimeItems ?? 0) + (y.otherAddBacks ?? 0)) * k;
  const adjusted = (y.netIncome + addBacks.reduce((s, a) => s + a.amount, 0)) * k;
  const available = adjusted - (input.existingDebtService ?? 0) + (input.outsideIncome ?? 0);
  const size = sizeFor(available, input);
  const dscr = newDebtService ? round2(coverageRatio(available, newDebtService)) : null;
  return {
    label: y.label, annualised: months < 12, adjustedCashFlow: Math.round(adjusted), availableForNewDebt: Math.round(available),
    sde: Math.round(sde), ...size, dscr, meetsTarget: dscr === null ? null : dscr >= input.dscrTarget, addBacks,
  };
}

function compare(y: YearFigures | undefined, b: Benchmarks | undefined): BenchmarkFlag[] {
  if (!y?.revenue || !b) return [];
  const rev = y.revenue;
  const flags: BenchmarkFlag[] = [];
  const check = (metric: BenchmarkFlag['metric'], label: string, yours: number, typical: number, higherIsWorse: boolean) => {
    const gap = yours - typical;
    if (Math.abs(gap) < 10) return;
    const worse = higherIsWorse ? gap > 0 : gap < 0;
    flags.push({
      metric, label, yoursPct: round2(yours), typicalPct: typical,
      note: worse
        ? `Your ${label.toLowerCase()} is ${Math.abs(Math.round(gap))} points ${gap > 0 ? 'above' : 'below'} what is typical for this industry. Expect a lender to ask why.`
        : `Your ${label.toLowerCase()} is ${Math.abs(Math.round(gap))} points ${gap > 0 ? 'above' : 'below'} typical — a strength worth explaining with your returns.`,
    });
  };
  if (y.cogs !== undefined) check('cogs', 'Cost of goods sold', (y.cogs / rev) * 100, b.cogsPct, true);
  if (y.operatingExpenses !== undefined) check('opex', 'Operating expenses', (y.operatingExpenses / rev) * 100, b.opexPct, true);
  const ebitda = y.netIncome + (y.interest ?? 0) + (y.depreciation ?? 0);
  check('margin', 'Operating margin (EBITDA)', (ebitda / rev) * 100, b.ebitdaPct, false);
  return flags;
}

export function cashFlowCapacity(input: CashFlowInput): CashFlowResult {
  if (!(input.dscrTarget > 0)) throw new Error('dscrTarget must be positive');
  const notes: string[] = [];
  const requested = input.requestedLoan && input.requestedLoan > 0 ? input.requestedLoan : null;
  const requestedMonthlyPayment = requested ? round2(monthlyPayment(requested, input.ratePct, input.termMonths)) : null;
  const newDebtService = requestedMonthlyPayment ? requestedMonthlyPayment * 12 : null;
  const addSalary = input.addBackOwnerSalary ?? input.dealType === 'acquisition';

  let years: YearFigures[] = input.years;
  let isProjection = false;
  if (input.dealType === 'startup' || years.length === 0) {
    const b = input.benchmark;
    if (!input.projectedRevenue || !b) throw new Error('A startup estimate needs projected revenue and an industry');
    isProjection = true;
    const ebitda = (input.projectedRevenue * b.ebitdaPct) / 100;
    years = [{ label: 'Projected year', revenue: input.projectedRevenue, netIncome: ebitda, ownerSalary: 0 }];
    notes.push(`No tax history, so cash flow is projected at this industry's typical ${b.ebitdaPct}% operating margin. Lenders lend to startups on projections only with strong equity, collateral and industry experience.`);
  }

  const results = years.map((y) => yearResult(y, input, addSalary, newDebtService));
  const limiting = results.reduce((lo, r) => (r.maxLoan < lo.maxLoan ? r : lo));
  const full = results.filter((r) => !r.annualised);
  const grewEveryYear = full.length >= 2 && full.every((r, i) => i === 0 || r.availableForNewDebt > full[i - 1]!.availableForNewDebt);
  const latest = full.at(-1);
  const growthMaxLoan = grewEveryYear && latest && latest.maxLoan > limiting.maxLoan ? latest.maxLoan : null;

  const dscrs = results.map((r) => r.dscr).filter((d): d is number => d !== null);
  const requestedMinDscr = dscrs.length ? Math.min(...dscrs) : null;
  const verdict = requestedMinDscr === null ? null
    : requestedMinDscr >= input.dscrTarget ? 'qualifies' : requestedMinDscr >= 1 ? 'borderline' : 'short';

  if (addSalary) {
    notes.push(input.replacementSalary
      ? "Owner's salary is added back, less the salary someone will still need to be paid."
      : "Owner's salary is added back. A lender may not allow all of it if you still need to live on it — enter a replacement salary to see the effect.");
  }
  if (input.outsideIncome) notes.push('Outside household income is counted, as a lender may in a global cash-flow review.');
  if (results.some((r) => r.annualised)) notes.push('Year-to-date figures are annualised, so seasonal businesses should treat that column with care.');
  if (growthMaxLoan) notes.push(`Cash flow grew every year. Most lenders still size on the lowest year (${limiting.label}); some will use ${latest!.label}, which supports up to $${growthMaxLoan.toLocaleString('en-US')}.`);
  if (results.some((r) => r.maxLoan >= MAX_7A)) notes.push('Capped at the $5,000,000 SBA 7(a) maximum.');
  const ytd = results.find((r) => r.annualised);
  if (ytd && latest && ytd.availableForNewDebt < latest.availableForNewDebt * 0.9) {
    notes.push(`Year-to-date cash flow is running below ${latest.label}. SBA asks lenders to confirm the business has not declined since its last year-end, so expect questions.`);
  }
  if (input.dealType === 'acquisition' && limiting.maxLoan >= 3_000_000) {
    notes.push('At this size an acquisition generally needs a Quality of Earnings report, and the lender sizes the loan on its earnings figure.');
  }

  return {
    dealType: input.dealType, years: results, limitingYear: limiting.label,
    maxLoan: limiting.maxLoan, maxMonthlyPayment: limiting.maxMonthlyPayment, growthMaxLoan, grewEveryYear,
    requestedLoan: requested, requestedMonthlyPayment, requestedMinDscr, verdict, isProjection,
    sbaRule: sbaRuleFor(input.dealType, results, requested, limiting.maxLoan, newDebtService, input),
    benchmarkFlags: isProjection ? [] : compare(years.filter((y) => (y.months ?? 12) === 12).at(-1), input.benchmark),
    notes,
  };
}
